import { useState, type DragEvent, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Carte, Erreur } from '../../composants/communs.tsx';
import { Icone } from '../../composants/icones.tsx';
import { useSession } from '../../composants/session.tsx';
import {
  Distribution,
  DISTRIBUTION_INITIALE,
  versCharge,
  type EtatDistribution,
} from './Distribution.tsx';
import { COTE_MAX, envoyerPhoto } from './reduire-image.ts';
import {
  CANAUX,
  LIBELLE_CANAL,
  type Canal,
  type ProduitCree,
  type Referentiel,
} from './types.ts';

/**
 * Création d'un produit — RG-019 à RG-029.
 *
 * La référence est facultative : laissée vide, la base en génère une
 * (`PRD-000001`). On ne force pas l'utilisateur à inventer un code pour un
 * produit qui n'en a pas besoin.
 */
export function FormulaireProduit({
  onCree,
}: {
  /**
   * Remonte les avertissements du serveur au PARENT.
   *
   * Ils ne s'affichent pas ici : ce formulaire se ferme des la creation
   * reussie, et un avertissement montre dans un composant qu'on demonte
   * aussitot n'est jamais lu. Or « ce produit n'est pas encore vendable »
   * est precisement ce qu'il faut avoir vu.
   */
  onCree: (avertissements: { code: string; message: string }[]) => void;
}): ReactNode {
  const [designation, setDesignation] = useState('');
  const [reference, setReference] = useState('');
  const [categorieId, setCategorieId] = useState('');
  const [uniteId, setUniteId] = useState('');
  const [canaux, setCanaux] = useState<Canal[]>(['DETAIL']);
  const [prix, setPrix] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [survole, setSurvole] = useState(false);
  const [distribution, setDistribution] =
    useState<EtatDistribution>(DISTRIBUTION_INITIALE);
  const [envoiPhotos, setEnvoiPhotos] = useState<string | null>(null);
  // Stock d'alerte — RG-186. Facultatif : laissé vide, le produit n'a
  // simplement aucun seuil, comme aujourd'hui pour tout produit créé sans
  // passer ensuite par la fiche « Seuil ».
  const [seuilAlerte, setSeuilAlerte] = useState('');
  const { boutiques, peut } = useSession();
  const { data: referentiel } = useQuery({
    queryKey: ['referentiel-catalogue'],
    queryFn: () => appeler<Referentiel>('/referentiel/catalogue'),
  });

  /**
   * Creation PUIS photos, dans cet ordre et pas l'inverse.
   *
   * Une photo se rattache a un produit : il doit exister d'abord. Et si un
   * envoi de photo echoue, le PRODUIT reste cree — on le signale sans
   * pretendre que la creation a rate, sinon l'utilisateur la recommencerait
   * et se retrouverait avec un doublon.
   */
  const mutation = useMutation({
    mutationFn: async (corps: Record<string, unknown>) => {
      const cree = await appeler<ProduitCree>('/produits', {
        methode: 'POST',
        corps,
      });

      const echecs: string[] = [];
      for (const [index, fichier] of photos.entries()) {
        setEnvoiPhotos(
          `Envoi de la photo ${String(index + 1)} sur ${String(photos.length)}…`,
        );
        try {
          await envoyerPhoto(cree.id, fichier);
        } catch (echec) {
          echecs.push(
            `${fichier.name} : ${echec instanceof Error ? echec.message : 'refusee'}`,
          );
        }
      }
      setEnvoiPhotos(null);

      const avertissementsSeuil: { code: string; message: string }[] = [];

      /**
       * Stock d'alerte — après la création, comme les photos.
       *
       * `seuil_reapprovisionnement` se règle par le même PATCH que la fiche
       * « Seuil » utilise déjà (RG-186) : pas de nouvel endpoint, pas de
       * nouveau champ côté serveur. Un échec ici ne défait pas la création.
       */
      if (seuilAlerte.trim() !== '') {
        try {
          await appeler(`/produits/${cree.id}`, {
            methode: 'PATCH',
            corps: { seuil_reapprovisionnement: Number(seuilAlerte) },
          });
        } catch (echec) {
          avertissementsSeuil.push({
            code: 'SEUIL_REFUSE',
            message:
              'Produit créé, mais le stock d’alerte n’a pas pu être enregistré — ' +
              `${echec instanceof Error ? echec.message : 'refusé'}. Réglez-le depuis la fiche « Seuil ».`,
          });
        }
      }

      return {
        ...cree,
        avertissements: [
          ...cree.avertissements,
          ...avertissementsSeuil,
          ...echecs.map((message, index) => ({
            code: `PHOTO_REFUSEE_${String(index)}`,
            message: `Produit cree, mais une photo n'a pas ete envoyee — ${message}`,
          })),
        ],
      };
    },
    onSuccess: (resultat) => {
      setDesignation('');
      setReference('');
      setPrix({});
      setPhotos([]);
      setSeuilAlerte('');
      onCree(resultat.avertissements);
    },
    onError: () => {
      setEnvoiPhotos(null);
    },
  });

  function basculer(valeur: Canal): void {
    setCanaux((actuels) =>
      actuels.includes(valeur)
        ? actuels.filter((canal) => canal !== valeur)
        : [...actuels, valeur],
    );
  }

  function deposer(evenement: DragEvent<HTMLLabelElement>): void {
    evenement.preventDefault();
    setSurvole(false);
    setPhotos(Array.from(evenement.dataTransfer.files));
  }

  function soumettre(evenement: SyntheticEvent): void {
    evenement.preventDefault();

    mutation.mutate({
      id: crypto.randomUUID(),
      reference: reference.trim() === '' ? null : reference.trim(),
      designation: designation.trim(),
      categorie_id: categorieId,
      unite_base_id: uniteId,
      canaux: canaux.map((valeur) => ({ canal: valeur })),
      /*
        Distribution et prix sont construits ensemble : les deux cases de
        `Distribution` commandent l'une et l'autre. « Partout » omet
        `distribution` pour laisser le defaut du serveur s'appliquer, sinon on
        figerait la liste des boutiques du jour sans les suivantes.

        Un canal sans prix laisse le produit non vendable sur ce canal
        (RG-029) : le serveur le signale en avertissement.
      */
      ...versCharge(distribution, canaux, boutiques, prix),
    });
  }

  const blocPrixParCanal = (
    <div className="pile" style={{ gap: 'var(--espace-2)' }}>
      {CANAUX.map((valeur) => (
        <div
          key={valeur}
          className="ligne"
          style={{
            justifyContent: 'space-between',
            background: 'var(--sable-appui)',
            borderRadius: 'var(--rayon-net)',
            padding: 'var(--espace-3)',
          }}
        >
          <label style={{ margin: 0, fontWeight: 400 }}>
            <input
              type="checkbox"
              style={{ width: 'auto', marginRight: 8 }}
              checked={canaux.includes(valeur)}
              onChange={() => {
                basculer(valeur);
              }}
            />
            {LIBELLE_CANAL[valeur]}
          </label>
          <input
            type="number"
            min={1}
            step={1}
            aria-label={`Prix ${LIBELLE_CANAL[valeur]}`}
            placeholder="prix en FCFA"
            style={{ maxWidth: 140 }}
            disabled={!canaux.includes(valeur)}
            value={prix[valeur] ?? ''}
            onChange={(evenement) => {
              setPrix((actuels) => ({ ...actuels, [valeur]: evenement.target.value }));
            }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <form onSubmit={soumettre} className="pile">
      <div className="grille grille--identite-prix" style={{ alignItems: 'start' }}>
        <Carte titre="Identité du produit" icone="produits">
          <div className="champs">
            <div className="champ">
              <label htmlFor="reference">Référence</label>
              <input
                id="reference"
                name="reference"
                placeholder="générée si laissée vide"
                value={reference}
                onChange={(evenement) => {
                  setReference(evenement.target.value);
                }}
              />
            </div>
            <div className="champ">
              <label htmlFor="designation">Désignation *</label>
              <input
                id="designation"
                name="designation"
                required
                placeholder="nom du produit"
                value={designation}
                onChange={(evenement) => {
                  setDesignation(evenement.target.value);
                }}
              />
            </div>
          </div>

          <div className="champ">
            <label htmlFor="categorie">Catégorie *</label>
            <select
              id="categorie"
              name="categorie"
              required
              value={categorieId}
              onChange={(evenement) => {
                setCategorieId(evenement.target.value);
              }}
            >
              <option value="">Sélectionner la catégorie…</option>
              {(referentiel?.categories ?? []).map((categorie) => (
                <option key={categorie.id} value={categorie.id}>
                  {categorie.libelle}
                </option>
              ))}
            </select>
          </div>

          <div className="champ">
            <label htmlFor="unite">Unité de base *</label>
            <select
              id="unite"
              name="unite"
              required
              value={uniteId}
              onChange={(evenement) => {
                setUniteId(evenement.target.value);
              }}
            >
              <option value="">Sélectionner l'unité…</option>
              {(referentiel?.unites ?? []).map((unite) => (
                <option key={unite.id} value={unite.id}>
                  {unite.libelle}
                </option>
              ))}
            </select>
          </div>
        </Carte>

        <Carte titre="Prix par canal" icone="monnaie">
          <p className="entete__sous" style={{ marginTop: 0 }}>
            Saisissez le prix de vente pour chaque canal. Un canal sans prix
            n'est pas vendable.
          </p>

          {blocPrixParCanal}

          <Distribution
            canauxDuProduit={canaux}
            valeur={distribution}
            onChange={setDistribution}
          />
        </Carte>
      </div>

      <div
        className={peut('produit.modifier') ? 'grille grille--deux-colonnes' : 'grille'}
        style={{ alignItems: 'start' }}
      >
        {peut('produit.modifier') && (
          <Carte titre="Paramètres de stock" icone="stock">
            <div className="champ" style={{ maxWidth: 320 }}>
              <label htmlFor="seuil-alerte">Stock d'alerte (facultatif)</label>
              <input
                id="seuil-alerte"
                name="seuil-alerte"
                type="number"
                min={0}
                step={1}
                placeholder="stock d'alerte du produit"
                value={seuilAlerte}
                onChange={(evenement) => {
                  setSeuilAlerte(evenement.target.value);
                }}
              />
              <span className="entete__sous">
                Sous ce niveau, le produit apparaît dans les alertes de
                réapprovisionnement. Laissez vide pour aucune alerte.
              </span>
            </div>
          </Carte>
        )}

        <Carte titre="Images du produit" icone="image">
          <label
            className={survole ? 'depot depot--survole' : 'depot'}
            onDragOver={(evenement) => {
              evenement.preventDefault();
              setSurvole(true);
            }}
            onDragLeave={() => {
              setSurvole(false);
            }}
            onDrop={deposer}
          >
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              aria-label="Photos du produit"
              onChange={(evenement) => {
                setPhotos(Array.from(evenement.target.files ?? []));
              }}
            />
            <div className="depot__icone">
              <Icone nom="image" taille={28} />
            </div>
            <div>
              Glissez vos images ici ou <span className="depot__lien">cliquez pour parcourir</span>
            </div>
            <div className="entete__sous" style={{ marginTop: 6 }}>
              Réduites à {COTE_MAX} px avant l'envoi.
            </div>
          </label>
          <div className="entete__sous" style={{ marginTop: 8, color: 'var(--teal)' }}>
            {photos.length === 0
              ? 'Aucune image sélectionnée'
              : `${String(photos.length)} photo(s) : ${photos.map((f) => f.name).join(', ')}`}
          </div>
        </Carte>
      </div>

      <Erreur erreur={mutation.error} />
      {envoiPhotos !== null && (
        <div className="entete__sous" role="status">
          {envoiPhotos}
        </div>
      )}

      <div>
        <button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Création…' : 'Créer le produit'}
        </button>
      </div>
    </form>
  );
}
